import React, { useState, useEffect } from 'react';
import { useApi } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Database, Play, Loader2, CheckCircle, RefreshCw, FlaskConical } from 'lucide-react';
import { DataProvenancePanel } from '@/components/DataProvenancePanel';
import { HazardSourcesCard } from '@/components/HazardSourcesCard';
import { PropertyAddressCard } from '@/components/PropertyAddressCard';
import { JobBCard } from '@/components/JobBCard';
import { JobACard } from '@/components/JobACard';

export default function EnrichmentPage() {
  const api = useApi();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(null);
  const [runResult, setRunResult] = useState(null);
  const [runningAll, setRunningAll] = useState(false);
  const [refresh, setRefresh] = useState(null);
  const [refreshRunning, setRefreshRunning] = useState(false);

  const loadRefresh = async () => {
    try { const { data } = await api.get('/data-refresh/status'); setRefresh(data); }
    catch (err) { console.error("API error:", err?.message); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadRefresh(); }, []);

  const runRefresh = async () => {
    setRefreshRunning(true);
    try { await api.post('/data-refresh/run'); await loadRefresh(); }
    catch (err) { console.error("API error:", err?.message); }
    setRefreshRunning(false);
  };

  const load = async (refresh = false) => {
    try {
      const { data } = await api.get(`/enrichment/status${refresh ? '?refresh=true' : ''}`);
      setStatus(data);
    } catch (err) { console.error("API error:", err?.message); }
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const runAgent = async (agentId) => {
    setRunning(agentId);
    setRunResult(null);
    try {
      const { data } = await api.post(`/enrichment/run/${agentId}?batch_size=15`);
      setRunResult(data);
      load();
    } catch (err) { console.error("API error:", err?.message); }
    setRunning(null);
  };

  const runAll = async () => {
    setRunningAll(true);
    setRunResult(null);
    try {
      const { data } = await api.post('/enrichment/run-all?batch_size=8');
      setRunResult({ agent: 'All Agents', entities_enriched: data.reduce((s, r) => s + (r.entities_enriched || 0), 0), agents_run: data.length });
      load();
    } catch (err) { console.error("API error:", err?.message); }
    setRunningAll(false);
  };

  if (loading) return <div data-testid="enrichment-loading"><Skeleton className="h-64 rounded-sm" /></div>;

  const coverage = status?.enrichment_coverage || {};
  const tiers = status?.tier_distribution || {};

  return (
    <div className="space-y-6" data-testid="enrichment-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" style={{ color: '#0D1B2A', fontFamily: "'IBM Plex Sans'" }}>Data Enrichment Agents</h1>
          <p className="text-sm mt-1" style={{ color: '#5C6B7A' }}>{status?.total_entities || 0} entities | Tiger ANZ 2000</p>
          <p className="text-[10px] uppercase tracking-wider mt-1" style={{ color: '#5C6B7A' }} data-testid="enrichment-as-at">
            Coverage as at {status?.computed_at ? new Date(status.computed_at).toLocaleTimeString() : '—'}{status?.cached ? ' (materialised)' : ''} ·{' '}
            <button type="button" onClick={() => load(true)} className="underline" style={{ color: '#1A4C7C' }} data-testid="enrichment-rescan-btn">rescan now</button>
          </p>
        </div>
        <Button data-testid="run-all-agents" onClick={runAll} disabled={runningAll} className="rounded-sm text-xs" style={{ background: '#1A4C7C' }}>
          {runningAll ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
          {runningAll ? 'Running...' : 'Run All Agents'}
        </Button>
      </div>

      {/* Run Result */}
      {runResult && (
        <Card className="p-4 rounded-sm border-l-4 bg-white" style={{ borderLeftColor: '#1D9E75' }} data-testid="run-result">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4" style={{ color: '#1D9E75' }} />
            <span className="text-sm font-semibold" style={{ color: '#1D9E75' }}>
              {runResult.agent} — {runResult.entities_enriched} entities enriched
              {runResult.agents_run ? ` across ${runResult.agents_run} agents` : ''}
            </span>
          </div>
        </Card>
      )}

      {/* What the data actually is — before any coverage number is read */}
      <DataProvenancePanel api={api} />

      {/* The government layers that answer for flood and bushfire */}
      <HazardSourcesCard api={api} />

      {/* Family 6 · M1 — the state address registers that resolve an AU address */}
      <PropertyAddressCard api={api} />

      {/* Family 6 · M4b — Job B, the 50-address test run */}
      <JobBCard />

      {/* Family 6 · Job A — the hazard tables (versioned, rollback) */}
      <JobACard />

      {/* Scheduled Data Refresh Pipeline */}
      {refresh && (
        <Card className="p-5 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white" data-testid="data-refresh-card"
          style={{ borderLeft: '3px solid #1A4C7C' }}>
          <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wider" style={{ color: '#0D1B2A' }}>Scheduled Data Refresh</h3>
              <p className="text-xs mt-0.5" style={{ color: '#5C6B7A' }}>
                Insured clients refresh first, the register rotates behind them — every {refresh.config.interval_hours}h, and stale entities
                ({'>'}{refresh.config.stale_days}d) are re-enriched the moment they're quoted.
              </p>
            </div>
            <Button onClick={runRefresh} disabled={refreshRunning} data-testid="run-refresh-btn"
              className="rounded-sm text-white text-xs font-semibold" style={{ background: '#1A4C7C' }}>
              {refreshRunning ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
              Run cycle now
            </Button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[['Insured entities', refresh.insured_entities, '#0D1B2A'],
              ['Stale insured', refresh.stale_insured, refresh.stale_insured > 0 ? '#B27516' : '#1D9E75'],
              ['Last cycle refreshed', refresh.latest ? (refresh.latest.insured_refreshed + refresh.latest.register_refreshed) : '—', '#1A4C7C'],
              ['Pipeline', refresh.config.enabled ? 'ON' : 'OFF', refresh.config.enabled ? '#1D9E75' : '#E24B4A']].map(([l, v, color]) => (
              <div key={l} className="p-3 rounded-sm" style={{ background: '#F5F7FA' }}>
                <p className="text-[10px] uppercase tracking-wider" style={{ color: '#5C6B7A' }}>{l}</p>
                <p className="text-lg font-bold mt-0.5" style={{ color }}>{typeof v === 'number' ? v.toLocaleString() : v}</p>
              </div>
            ))}
          </div>
          {refresh.latest && (
            <p className="text-[10px] mt-3" style={{ color: '#5C6B7A' }} data-testid="refresh-last-run">
              Last run {String(refresh.latest.at).slice(0, 16).replace('T', ' ')} UTC by {refresh.latest.triggered_by} —
              {' '}{refresh.latest.insured_refreshed} insured + {refresh.latest.register_refreshed} register refreshed in {refresh.latest.duration_ms}ms
              {refresh.latest.dq && ` · DQ: ${Object.entries(refresh.latest.dq).filter(([, n]) => n > 0).map(([k, n]) => `${n} ${k.replace('missing_', 'no ')}`).join(', ') || 'clean'}`}
            </p>
          )}
        </Card>
      )}

      {/* Coverage Overview */}
      <Card className="p-5 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white" data-testid="coverage-overview">
        <h3 className="text-sm font-semibold uppercase tracking-wider" style={{ color: '#0D1B2A' }}>Enrichment Coverage</h3>
        <p className="text-xs mt-1 mb-4" style={{ color: '#5C6B7A' }}>
          {status?.sourcing_note || 'Coverage measures how much of the register carries a value, not how much of it was measured.'}
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Object.entries(coverage).map(([field, data]) => (
            <div key={field} className="p-3 rounded-sm" style={{ background: '#F5F7FA' }}>
              <p className="text-[10px] uppercase tracking-wider capitalize" style={{ color: '#5C6B7A' }}>{field.replace(/_/g, ' ')}</p>
              <div className="flex items-end gap-2 mt-1">
                <span className="text-lg font-bold" style={{ color: '#0D1B2A' }}>{data.pct}%</span>
                <span className="text-[10px] mb-0.5" style={{ color: '#5C6B7A' }}>{data.count} entities</span>
              </div>
              <div className="h-1.5 rounded-full overflow-hidden mt-2" style={{ background: 'rgba(136,150,168,0.15)' }}>
                <div className="h-full rounded-full" style={{ width: `${data.pct}%`, background: data.pct > 70 ? '#1D9E75' : data.pct > 40 ? '#E8A020' : '#E24B4A' }} />
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Tier Distribution */}
      <Card className="p-5 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white">
        <h3 className="text-sm font-semibold uppercase tracking-wider mb-4" style={{ color: '#0D1B2A' }}>Tiger ANZ 2000 Tier Distribution</h3>
        <div className="grid grid-cols-4 gap-3">
          {[
            { tier: 1, label: 'Elite Top 100', capture: '60%', refresh: 'Weekly', color: '#1D9E75' },
            { tier: 2, label: 'Growth 300', capture: '40%', refresh: 'Monthly', color: '#1A4C7C' },
            { tier: 3, label: 'Regional 500', capture: '10%', refresh: 'Quarterly', color: '#E8A020' },
            { tier: 4, label: 'Satellite 1000+', capture: '5%', refresh: 'Annual', color: '#5C6B7A' },
          ].map(t => (
            <div key={t.tier} className="p-3 rounded-sm border border-[rgba(136,150,168,0.15)]">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 rounded-full" style={{ background: t.color }} />
                <span className="text-xs font-semibold" style={{ color: '#0D1B2A' }}>Tier {t.tier}</span>
              </div>
              <p className="text-lg font-bold" style={{ color: '#0D1B2A' }}>{tiers[`tier_${t.tier}`] || 0}</p>
              <p className="text-[10px]" style={{ color: '#5C6B7A' }}>{t.label}</p>
              <p className="text-[10px]" style={{ color: '#5C6B7A' }}>Capture: {t.capture} | {t.refresh}</p>
            </div>
          ))}
        </div>
      </Card>

      {/* Agent List */}
      <Card className="rounded-sm border border-[rgba(136,150,168,0.2)] bg-white" data-testid="agent-list">
        <div className="p-4 border-b border-[rgba(136,150,168,0.2)]">
          <h3 className="text-sm font-semibold uppercase tracking-wider" style={{ color: '#0D1B2A' }}>Agent Fleet</h3>
        </div>
        <div className="divide-y divide-[rgba(136,150,168,0.1)]">
          {(status?.agents || []).map(agent => (
            <div key={agent.id} className="p-4 flex items-center justify-between hover:bg-[#F5F7FA] transition-colors">
              <div className="flex items-center gap-4">
                <div className="w-8 h-8 rounded-sm flex items-center justify-center" style={{ background: 'rgba(26,76,124,0.1)' }}>
                  <Database className="w-4 h-4" style={{ color: '#1A4C7C' }} />
                </div>
                <div>
                  <p className="text-sm font-medium" style={{ color: '#0D1B2A' }}>{agent.name}</p>
                  <p className="text-xs flex items-center gap-1" style={{ color: '#B27516' }}>
                    <FlaskConical className="w-3 h-3" /> {agent.source}
                  </p>
                  {agent.intended_source && (
                    <p className="text-[10px]" style={{ color: '#5C6B7A' }}>Designed to read: {agent.intended_source}</p>
                  )}
                  <p className="text-[10px]" style={{ color: '#5C6B7A' }}>{agent.fields}</p>
                </div>
              </div>
              <Button data-testid={`run-agent-${agent.id}`} size="sm" onClick={() => runAgent(agent.id)} disabled={running === agent.id}
                className="rounded-sm text-xs" style={{ background: '#1A4C7C' }}>
                {running === agent.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3 mr-1" />}
                {running === agent.id ? 'Running...' : 'Run'}
              </Button>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
